import React, { useMemo, useRef, useEffect } from 'react';
import { Form, TextButton, Box, Typography } from 'bonfire-kindling';
import { useTranslation } from 'react-i18next';
import _uniq from 'lodash/uniq';
import { InputGroup, StackedList } from 'components';

// Exported for testing only.
export const toArray = keywords => {
  const keywordsArray = keywords.split(',').map(word => word.trim());

  return keywordsArray
    .map(keyword => {
      // Since we split the string above on every comma, sometimes there can
      // be keywords that are enclosed in double quotations but also contain commas.
      // E.g. 'Apple, pear, "cheddar, cheese"'.
      // We then need to check if the keyword contains an uneven amount of quotes
      // and strip it.
      if (keyword.indexOf('"') !== -1 && !/^"[^"]+"$/.test(keyword)) {
        return keyword.replace('"', '');
      }

      return keyword;
    })
    .filter(Boolean);
};

const Keywords = ({
  keywords,
  formId,
  onSubmit: _onSubmit,
  onAddKeyword,
  onRemoveKeyword,
  isMutating = false,
}) => {
  const { t } = useTranslation();
  const timeoutRef = useRef();

  const validateKeywords = dirtyKeywords => {
    // Allow each word to optionally be enclosed in double quotations,
    // but allow no double quotes beyond that.
    // See tests for examples.
    const isValid = word => /^"[^"]+"$|^[^"]+$/.test(word);

    if (dirtyKeywords) {
      const words = toArray(dirtyKeywords);
      if (!words.every(isValid)) {
        return t('settings.keywords.invalid');
      }
    }

    return undefined;
  };

  const reset = form => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(form.reset);
  };

  const addKeywords = form => {
    const { values } = form.getState();
    const newKeywords = _uniq([...keywords, ...toArray(values.keywords)]);

    if (onAddKeyword) {
      onAddKeyword(newKeywords);
    }

    return newKeywords;
  };

  const removeKeyword = keyword => {
    const newKeywords = keywords.filter(word => word !== keyword);

    if (onRemoveKeyword) {
      onRemoveKeyword(newKeywords);
    }

    return newKeywords;
  };

  const onSubmit = async (_, form) => {
    await _onSubmit(addKeywords(form));
    reset(form);
  };

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const sortedKeywords = useMemo(() => keywords.sort(), [keywords]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        '& > * + *': {
          marginTop: theme => theme.spacing(2),
        },
      }}
    >
      <Form onSubmit={onSubmit} initialValues={{ keywords: undefined }}>
        {({ handleSubmit, submitting, pristine, hasValidationErrors }) => (
          <form onSubmit={handleSubmit} id={formId}>
            <InputGroup sx={{ width: 'initial', maxWidth: '25rem' }}>
              <InputGroup.FormTextField
                name="keywords"
                placeholder={t('settings.keywords.add_keywords_desc')}
                useFieldConfig={{ validate: validateKeywords }}
                inputProps={{ 'aria-label': t('settings.keywords.add_keywords_desc') }}
                required
              />
              <InputGroup.Button
                type="submit"
                disabled={pristine || hasValidationErrors || isMutating}
                isLoading={submitting}
                label={t('settings.keywords.add')}
              />
            </InputGroup>
          </form>
        )}
      </Form>
      {!!sortedKeywords.length && (
        <StackedList title={t('settings.keywords.stacked_list_title')}>
          {sortedKeywords.map(keyword => (
            <StackedList.Item key={keyword}>
              <Typography
                sx={{ fontWeight: 600, fontSize: '13px', ml: 3, wordBreak: 'break-word' }}
              >
                {keyword}
              </Typography>
              <TextButton
                variant="text"
                color="error"
                disabled={isMutating}
                onClick={() => removeKeyword(keyword)}
                label={t('settings.keywords.remove')}
                sx={{
                  textDecoration: 'underline',
                  fontSize: '13px',
                  fontWeight: '400',
                  color: theme => theme.palette.brand.red,
                  marginRight: theme => theme.spacing(3),
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              />
            </StackedList.Item>
          ))}
        </StackedList>
      )}
    </Box>
  );
};

export default Keywords;
